import moment from 'moment';
import { environment } from 'src/environments/environment';
import { clientLoggerConfig, i18nJargon } from 'src/environments/env.config';
import { DOCUMENT } from '@angular/common';
import {
  HttpClient,
  HttpBackend,
  HttpErrorResponse,
} from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AuthClientConfig, AuthService } from '@auth0/auth0-angular';
import {
  catchError,
  filter,
  map,
  retry,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  NEVER,
  Observable,
  forkJoin,
  lastValueFrom,
  of,
  throwError,
} from 'rxjs';
import {
  WHLayoutConfigService,
  WHLayoutNavItemDOM,
  WHLayoutLoggedInUserDOM,
  WHLayoutLanguageDOM,
  WHThemeConfigService,
  WHLayoutConfigDOM,
  IWHLanguageDTO,
  IWHTenantDTO,
  RequestTranslationCommand,
  WHI18nDataService,
  WHIconENUM,
  initSplashScreen,
  WHSplashScreenTypeENUM,
  SplashScreenConfigDOM,
  primaryColor,
  WHLoginDataService,
  WHFeatureConfigDataService,
  FLOW_GATEWAY,
  MGMT_GATEWAY,
  IWHFeatureConfigDTO,
  WHMetadataDataService,
  IWHFlowMetadataDTO,
  setRegisterLocaleData,
  IWHEnumWithTranslationsDTO,
  WHFlowMetadataDOM,
  IWHUserDTO,
  WHActiveUserDOM,
  WHActiveTenantDOM,
  themeConfig,
  sanitizeFeatureConfig,
  sanitizePagesUi,
  WHLayoutConfigTypeENUM,
  WHLogMessage,
  WHNgxLoggerService,
  WHRegisterLocaleDataENUM,
  MGMT_v1_GATEWAY,
  countryCodes
} from '@workheld/workheld-shared-lib';
import {
  layoutNavItemDOMList,
  layoutLoggedInUserNavItemDOMList,
  registredLayoutConfigDOMList,
} from './app.config';
import { Route, Router } from '@angular/router';
import { routes } from './app-routing.module';
const loadBryntumLocale = () =>
  import('src/app/app-pages/w-h-team-planner-page/team-planner-configs/team-planner-locale.config').catch(
    () => null,
  );
import { UIService } from './app-services/ui.service';
import { RemindTechnicianService } from './app-services/remind-technician.service';
import {
  AVAILABLE_LANGUAGES,
  ENV_SETTINGS_URL,
  IEnvironmentSettings,
  InitContext,
} from './app-shared/constants/initializer-constants';

const ENV_SETTINGS_RETRY = { count: 2, delay: 1000 };

const languageCountryCodeMap: Record<string, string> = {
  en: 'GB',
  cs: 'CZ',
  sl: 'SI',
  de: 'AT'
};

@Injectable()
export class AppInitializerService {
  private splashScreenElem: HTMLElement;

  private handler: HttpBackend;
  private authClientConfig: AuthClientConfig;
  private ngxLoggerService: WHNgxLoggerService;

  private clientLoggerConfig: WHLogMessage = clientLoggerConfig;

  constructor(
    handler: HttpBackend,
    authClientConfig: AuthClientConfig,
    @Inject(DOCUMENT) private document: Document,
    private remindTechnicianService: RemindTechnicianService,
    private router: Router,
    private uiService: UIService,
    private injector: Injector,
    private httpClient: HttpClient,
  ) {
    // CONF
    this.handler = handler;
    this.authClientConfig = authClientConfig;
  }

  public initializeApp(): Promise<void> {
    // SPLASH SCREEN PLAY
    this.playSplashScreen(WHSplashScreenTypeENUM.WORKHELD_FLOW, {
      fillColor: primaryColor,
      backgroundImageUrl: './assets/img/loading.gif',
    });

    return new Promise<void>((resolveInit, rejectInit) => {
      // INIT CORE SERVICES
      this.ngxLoggerService = this.injector.get(WHNgxLoggerService);
      const ctx: InitContext = {
        translateService: this.injector.get(TranslateService),
        loginDataService: this.injector.get(WHLoginDataService),
        i18nDataService: this.injector.get(WHI18nDataService),
        featureConfigDataService: this.injector.get(WHFeatureConfigDataService),
        themeConfigService: this.injector.get(WHThemeConfigService),
        layoutConfigService: this.injector.get(WHLayoutConfigService),
        metadataDataService: this.injector.get(WHMetadataDataService),
        authClientConfig: this.authClientConfig,
        clientLoggerConfig: this.clientLoggerConfig,
      };

      lastValueFrom(
        new HttpClient(this.handler)
          .get<IEnvironmentSettings>(ENV_SETTINGS_URL)
          .pipe(
            retry(ENV_SETTINGS_RETRY),
            // without these settings Auth0 is never configured, so
            // bootstrapping anyway only crashes the AuthGuard on the first
            // navigation (FLOW-WEB-38) — stay on the splash screen instead
            catchError(() => {
              this.showEnvSettingsLoadError();
              return NEVER;
            }),
            switchMap((env) => this.initEnvironment(env, ctx)),
            switchMap(() => this.loading(ctx.authService)),
            switchMap(() => this.loadCoreData(ctx)),
            switchMap((result) => this.applyCoreData(result, ctx)),
            switchMap((e) => this.loadEnumTranslations(e, ctx)),
            map((enums) => this.applyEnumTranslations(enums, ctx)),
          ),
      )
        .then(() => this.finalizeInit(resolveInit))
        .catch(() => this.finalizeInit(resolveInit));
    });
  }

  private showEnvSettingsLoadError(): void {
    if (!this.splashScreenElem) return;

    // do not use translation keys: translations are not loaded yet
    const message = this.document.createElement('p');
    message.textContent =
      'Could not load. Please check your connection and reload. / Konnte nicht geladen werden. Bitte prüfen Sie Ihre Verbindung und laden Sie neu.';
    const reloadButton = this.document.createElement('button');
    reloadButton.type = 'button';
    reloadButton.textContent = 'Reload / Neu laden';
    reloadButton.addEventListener('click', () => window.location.reload());

    Object.assign(this.splashScreenElem.style, {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      textAlign: 'center',
      fontFamily: 'sans-serif',
    });
    this.splashScreenElem.replaceChildren(message, reloadButton);
  }

  private finalizeInit(resolve: () => void): void {
    this.remindTechnicianService.setAlertOptions();
    this.ngxLoggerService.setClientConfig(this.clientLoggerConfig);
    resolve();
    if (this.splashScreenElem) {
      this.splashScreenElem.remove();
    }
  }

  private initEnvironment(
    environmentSettings: IEnvironmentSettings,
    ctx: InitContext,
  ) {
    const auth0Config: any = {};

    this.initializeEnvironment(environmentSettings);
    this.clientLoggerConfig.stage = environment.stage;
    this.configureRoutes(environment.apiUrl);
    Object.entries(environment.auth0Config).forEach((property: any[]) => {
      auth0Config[property[0]] = property[1];
    });

    // init authClientConfig and service
    this.authClientConfig.set(auth0Config);
    ctx.authService = this.injector.get(AuthService);

    return ctx.authService.isLoading$.pipe(take(1));
  }

  private loading(authService: AuthService): Observable<boolean> {
    return authService.isLoading$.pipe(
      filter((loading) => !loading), // wait until loading is false
      take(1), // complete after the first false
    );
  }

  private loadCoreData(ctx: InitContext) {
    return forkJoin({
      user: this.getUserData(ctx),
      permissions: this.loadPermissions(ctx),
      tenant: this.getTenantData(ctx),
      languages: this.getLanguages(ctx),
      metadata: this.getMetadata(ctx),
      featureConfigs: this.getFeatureConfigs(ctx),
      flowStatus: this.getFlowStatus(ctx),
    });
  }

  private applyCoreData(
    coreData: {
      user: IWHUserDTO;
      permissions: string[];
      tenant: IWHTenantDTO;
      languages: IWHLanguageDTO[];
      metadata: IWHFlowMetadataDTO;
      featureConfigs: IWHFeatureConfigDTO[];
      flowStatus: boolean;
    },
    ctx: InitContext,
  ) {
    const {
      user,
      permissions,
      tenant,
      metadata,
      languages,
      featureConfigs,
      flowStatus,
    } = coreData;

    // Flow deactivated for this tenant - block access, don't apply any core data.
    if (flowStatus === false) {
      this.logout(ctx.authService);
      return throwError(() => new Error('Flow product is not active for this tenant'));
    }

    // SET USER DATA
    this.setLoginData(user, ctx.loginDataService, ctx.layoutConfigService);

    // SET PERMISSIONS
    ctx.loginDataService.setPermissions(permissions);

    // SET TENANT DATA
    this.setTenantData(
      tenant,
      ctx.loginDataService,
      ctx.layoutConfigService,
      ctx.themeConfigService,
    );

    // SET FEATURE CONFIGS
    this.setFeatureConfigData(
      featureConfigs,
      ctx.loginDataService,
      ctx.layoutConfigService,
    );

    // SET METADATA
    if (metadata) {
      ctx.metadataDataService.setMetadataDOM(new WHFlowMetadataDOM(metadata));
    }

    return forkJoin({
      user: of(user),
      reqArray: this.setLanguages(languages, ctx),
    });
  }

  private loadEnumTranslations(
    data: { reqArray: any[]; user: any },
    ctx: InitContext,
  ) {
    // APPLY TRANSLATIONS
    this.applyTranslations(data.reqArray, ctx.translateService);

    const langKey: string = this.getLangKey(data.user, ctx.translateService);

    return ctx.metadataDataService
      .getFlowEnumTranslations(
        {
          apiUrl: environment.apiUrl + MGMT_v1_GATEWAY,
        },
        {
          language: langKey,
          type: undefined,
          tenantToken: ctx.loginDataService.activeTenantDOM$.value.token,
        },
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.logout(ctx.authService, error);
          return of(null);
        }),
      );
  }

  private applyEnumTranslations(
    enumTranslations: IWHEnumWithTranslationsDTO[],
    ctx: InitContext,
  ): boolean {
    if (enumTranslations) {
      ctx.metadataDataService.setMetadataTranslation(enumTranslations);
    }
    return true;
  }

  private setLanguages(
    languages: IWHLanguageDTO[],
    ctx: InitContext,
  ): Observable<any[]> {
    this.setLanguageData(ctx);

    // current timestamp
    const lastUpdated = localStorage.getItem('wh_translations_last_updated');
    const oneDay = 24 * 60 * 60 * 1000; // 1 day in milliseconds

    // SET LANGUAGES
    const featureConfigMap: Map<string, boolean> =
      ctx.loginDataService.featureConfigMap$.value;

    const langs: string[] = [];
    const languageDOMList: WHLayoutLanguageDOM[] = [];

    for (const element of languages) {
      const language: IWHLanguageDTO = element;
      const featureKey: string = `language.${language.key}.enabled`;
      const enabled: boolean = featureConfigMap.has(featureKey)
        ? featureConfigMap.get(featureKey)
        : false;

      const translateKey: string = `app.language.${language.key}.label`;
      const countryCode = languageCountryCodeMap[language.key] ?? language.key;
      const languageIcon =
        countryCodes.find(
          (country) => country.code.toLowerCase() === countryCode.toLowerCase(),
        )?.flag_code ?? WHIconENUM.LanguageIcon;
      const layoutLanguage: WHLayoutLanguageDOM = new WHLayoutLanguageDOM({
        featureKey,
        enabled,
        translateKey,
        icon: languageIcon,
      }).fromLanguageDTO(language);

      langs.push(language.key);
      languageDOMList.push(layoutLanguage);
    }

    ctx.loginDataService.setActiveLanguages(languageDOMList as any);
    const jargonToAdd: string[] = i18nJargon.filter((jargonKey) =>
      jargonKey.includes(ctx.loginDataService.activeTenantDOM$.value.token),
    );
    ctx.translateService.addLangs(langs.concat(jargonToAdd));

    setRegisterLocaleData(langs);
    ctx.layoutConfigService.setLayoutLanguageDOMList(languageDOMList);

    // CHECK LOCAL STORAGE FOR TRANSLATIONS
    const allKeys = langs.concat(jargonToAdd);
    const cachedTranslations = allKeys.map((languageKey) => {
      return localStorage.getItem(`wh_translations_${languageKey}`)
        ? JSON.parse(localStorage.getItem(`wh_translations_${languageKey}`)!)
        : null;
    });
    const tenantToken = ctx.loginDataService.activeTenantDOM$.value.token;
    const sameTenant = localStorage.getItem('wh_tenant_token') === tenantToken;

    const cacheIsValid =
      lastUpdated && Date.now() - Number(lastUpdated) < oneDay;
    if (!cachedTranslations.includes(null) && sameTenant && cacheIsValid) {
      return of(cachedTranslations);
    }

    for (const key of allKeys) {
      localStorage.removeItem(`wh_translations_${key}`);
    }

    localStorage.setItem('wh_translations_last_updated', Date.now().toString());
    localStorage.setItem('wh_tenant_token', tenantToken);

    // ---- STEP 1: fetch CURRENT LANGUAGE first ----

    const userDefaultLang =
      ctx.loginDataService.activeUserDOM$.value.language.key;
    const languageKey = ctx.translateService.defaultLang
      ? ctx.translateService.defaultLang
      : userDefaultLang;

    return ctx.i18nDataService
      .getClientTranslations(
        { apiUrl: environment.apiUrl + MGMT_GATEWAY },
        new RequestTranslationCommand({
          languageKey,
          tenantToken: ctx.loginDataService.activeTenantDOM$.value.token,
          removePrefix: true,
        }).initFlowClientTranslationKeys(),
      )
      .pipe(
        tap((translations) => {
          localStorage.setItem(
            `wh_translations_${languageKey}`,
            JSON.stringify(translations),
          );
        }),
        switchMap((currentTranslations) => {
          const otherKeys = allKeys.filter(
            (key) =>
              key !== languageKey &&
              !localStorage.getItem(`wh_translations_${key}`),
          );

          if (otherKeys.length > 0) {
            const lazyRequests$ = otherKeys.map((key) =>
              ctx.i18nDataService
                .getClientTranslations(
                  { apiUrl: environment.apiUrl + MGMT_GATEWAY },
                  new RequestTranslationCommand({
                    languageKey: key,
                    tenantToken:
                      ctx.loginDataService.activeTenantDOM$.value.token,
                    removePrefix: true,
                  }).initFlowClientTranslationKeys(),
                )
                .pipe(
                  tap((translations) =>
                    localStorage.setItem(
                      `wh_translations_${key}`,
                      JSON.stringify(translations),
                    ),
                  ),
                ),
            );

            // launch in background (don’t block init)
            ctx.layoutConfigService.setLanguagesLoading(true);

            forkJoin(lazyRequests$).subscribe({
              next: (rest) => {
                this.applyTranslations(rest, ctx.translateService);
                ctx.layoutConfigService.setLanguagesLoading(false);
              },
              error: (err) => {
                ctx.layoutConfigService.setLanguagesLoading(false);
                console.warn('Lazy-load translations failed', err);
              },
            });
          }
          return of([currentTranslations]);
        }),
      );
  }

  private getLangKey(
    user: IWHUserDTO,
    translateService: TranslateService,
  ): string {
    return (
      localStorage.getItem('whlang') ??
      user?.language?.key ??
      (Object.values(WHRegisterLocaleDataENUM).includes(
        translateService.getBrowserLang() as any,
      )
        ? translateService.getBrowserLang()!
        : 'de')
    );
  }

  private getFeatureConfigs(
    ctx: InitContext,
  ): Observable<IWHFeatureConfigDTO[] | null> {
    return ctx.featureConfigDataService
      .getFlowFeatureConfigDTOList({
        apiUrl: environment.apiUrl + MGMT_GATEWAY,
      })
      .pipe(
        catchError((err) => {
          this.logout(ctx.authService, err);
          return of(null);
        }),
      );
  }

  private getUserData(ctx: InitContext): Observable<IWHUserDTO | null> {
    return ctx.loginDataService
      .getMe({ apiUrl: environment.apiUrl + MGMT_GATEWAY })
      .pipe(
        catchError((err) => {
          this.logout(ctx.authService, err);
          return of(null);
        }),
      );
  }

  private loadPermissions(ctx: InitContext) {
    return ctx.loginDataService
      .getMyPermissions({
        apiUrl: environment.apiUrl + MGMT_GATEWAY,
      })
      .pipe(
        catchError((err) => {
          this.logout(ctx.authService, err);
          return of(null);
        }),
      );
  }

  private getTenantData(ctx: InitContext): Observable<IWHTenantDTO | null> {
    return ctx.loginDataService
      .getMyTenant({
        apiUrl: environment.apiUrl + MGMT_GATEWAY,
      })
      .pipe(
        catchError((err) => {
          this.logout(ctx.authService, err);
          return of(null);
        }),
      );
  }

  private getLanguages(ctx: InitContext): Observable<IWHLanguageDTO[]> {
    return ctx.i18nDataService
      .getLanguageDTOList({
        apiUrl: environment.apiUrl + MGMT_GATEWAY,
      })
      .pipe(
        catchError((err) => {
          this.logout(ctx.authService, err);
          return of(null);
        }),
      );
  }

  private getFlowStatus(ctx: InitContext): Observable<boolean> {
    return this.httpClient
      .get<boolean>(
        `${environment.apiUrl}${MGMT_v1_GATEWAY}/users/me/flow-status`,
      )
      .pipe(
        catchError((err) => {
          this.logout(ctx.authService, err);
          return of(false);
        }),
      );
  }

  private getMetadata(ctx: InitContext): Observable<IWHFlowMetadataDTO | null> {
    return ctx.metadataDataService
      .getFlowMetadataDTO({ apiUrl: environment.apiUrl + FLOW_GATEWAY })
      .pipe(
        catchError((err) => {
          this.logout(ctx.authService, err);
          return of(null);
        }),
      );
  }

  private configureRoutes(apiUrl: string) {
    if (apiUrl) {
      routes.forEach((route: Route) => {
        if (route.children) {
          route.children.forEach((childRoute: Route) => {
            if (childRoute['data']) {
              Object.assign(childRoute['data'], {
                apiUrl,
              });
            }
          });
        }
      });
      environment.auth0Config.httpInterceptor.allowedList = [`${apiUrl}*`];
    }
    this.router.resetConfig(routes);
  }

  private applyTranslations(
    respArray: any[],
    translateService: TranslateService,
  ) {
    respArray.forEach((translations) => {
      this.uiService.enTranslations = respArray.find(
        (translations) => translations.en,
      );
      Object.entries(translations).forEach((property: any[]) => {
        const lang = property[0];
        const translation = property[1];
        // Apply translation to TranslateService
        translateService.setTranslation(lang, translation, true);
        // Apply translation to Bryntum locale (lazy loaded)
        loadBryntumLocale()
          .then((m) => m?.extendBryntumLocale(lang, translation))
          .catch(() => {});
      });
    });
  }

  private setLanguageData(ctx: InitContext) {
    const activeUserDOM: WHActiveUserDOM =
      ctx.loginDataService.activeUserDOM$.value;
    const activeTenantDOM: WHActiveTenantDOM =
      ctx.loginDataService.activeTenantDOM$.value;

    if (!activeTenantDOM) {
      this.logout(ctx.authService, 'No active tenant found');
    }
    const browserLang: string = ctx.translateService.getBrowserLang();
    let userPrefLang: string = localStorage.getItem('whlang');

    ctx.loginDataService.featureConfigMap$.subscribe((s) => {
      if (
        !AVAILABLE_LANGUAGES.includes(userPrefLang) ||
        !AVAILABLE_LANGUAGES.includes(activeUserDOM?.language?.key)
      ) {
        userPrefLang = 'en';
      }
    });

    if (userPrefLang) {
      ctx.translateService.setFallbackLang(userPrefLang);
      ctx.translateService.use(userPrefLang);
      loadBryntumLocale()
        .then((m) => m?.applyBryntumLocale(userPrefLang))
        .catch(() => {});
      moment.locale(userPrefLang);
    } else if (activeUserDOM?.language?.key) {
      ctx.translateService.setFallbackLang(activeUserDOM.language.key);
      const lngKey: string = ctx.translateService.langs?.includes(
        activeUserDOM.language.key + '_' + activeTenantDOM.token,
      )
        ? activeUserDOM.language.key + '_' + activeTenantDOM.token
        : activeUserDOM.language.key;

      ctx.translateService.use(lngKey);
      loadBryntumLocale()
        .then((m) => m?.applyBryntumLocale(activeUserDOM.language.key))
        .catch(() => {});
      moment.locale(activeUserDOM.language.key);
    } else if (ctx.translateService.langs?.includes(browserLang)) {
      ctx.translateService.setFallbackLang(browserLang);
      const lngKey: string = ctx.translateService.langs.includes(
        browserLang + '_' + activeTenantDOM.token,
      )
        ? browserLang + '_' + activeTenantDOM.token
        : browserLang;
      ctx.translateService.use(lngKey);
      loadBryntumLocale()
        .then((m) => m?.applyBryntumLocale(browserLang))
        .catch(() => {});
      moment.locale(browserLang);
    } else {
      ctx.translateService.setFallbackLang('en');
      ctx.translateService.use('en');
      loadBryntumLocale()
        .then((m) => m?.applyBryntumLocale('en'))
        .catch(() => {});
      moment.locale('en');
    }
  }

  private setLoginData(
    userDTO: IWHUserDTO,
    loginDataService: WHLoginDataService,
    layoutConfigService: WHLayoutConfigService,
  ): void {
    this.clientLoggerConfig.loggedIn = userDTO.id;
    this.clientLoggerConfig.loggerName =
      userDTO.firstName + ' ' + userDTO.lastName;

    // SAVE USER STATE
    loginDataService.platformAdmin = userDTO.platformAdmin;
    loginDataService.partnerAdmin = userDTO.partnerAdmin;
    loginDataService.setActiveUser(new WHActiveUserDOM(userDTO));

    // SET LOGGED USER
    layoutConfigService.setLayoutLoggedInUserDOM(
      new WHLayoutLoggedInUserDOM(userDTO),
    );
  }

  private logout(authService: AuthService, error?: any): void {
    localStorage.removeItem('leftOffPath');
    authService.logout({
      logoutParams: { returnTo: window.location.origin },
    });
  }

  private setTenantData(
    tenantDTO: IWHTenantDTO,
    loginDataService: WHLoginDataService,
    layoutConfigService: WHLayoutConfigService,
    themeConfigService: WHThemeConfigService,
  ): void {
    this.clientLoggerConfig.tenant = tenantDTO.id;

    // SAVE TENANT STATE
    loginDataService.setActiveTenant(new WHActiveTenantDOM(tenantDTO));

    // INIT LAYOUT
    layoutConfigService.setRegistredLayoutConfigDOMList(
      registredLayoutConfigDOMList,
    );
    layoutConfigService.setActiveLayoutConfigDOM(
      new WHLayoutConfigDOM().initConfigType(WHLayoutConfigTypeENUM.ALPHA),
    );

    // INIT THEME
    layoutConfigService.tenantCustomization(tenantDTO.customization);
    themeConfigService.tenantCustomization(
      tenantDTO.customization,
      themeConfig,
    );
  }

  private setFeatureConfigData(
    featureConfigDTOList: IWHFeatureConfigDTO[],
    loginDataService: WHLoginDataService,
    layoutConfigService: WHLayoutConfigService,
  ): void {
    // SET FEATURE CONFIG DATA
    loginDataService.setFeatureConfigMap(featureConfigDTOList);

    // FEATURE CONFIG MAP
    const featureConfigMap: Map<string, boolean> =
      loginDataService.featureConfigMap$.value;
    const devFeatureConfigMap: Map<string, boolean> =
      environment.devFeatureConfigMap;

    Array.from(devFeatureConfigMap.keys()).forEach((key: string) => {
      const value: boolean = devFeatureConfigMap.get(key);
      featureConfigMap.set(key, value);
    });
    loginDataService.featureConfigMap$.next(featureConfigMap);

    // NAVIGATION
    const sanitizedNavItems: WHLayoutNavItemDOM[] = sanitizeFeatureConfig(
      layoutNavItemDOMList,
      featureConfigMap,
      loginDataService.activeUserDOM$.value.team?.subcontractor,
    ) as WHLayoutNavItemDOM[];

    layoutConfigService.setLayoutNavItemDOMList(sanitizedNavItems);
    // SET LOGGED USER NAV ITEMS - ADD PERMISIONS
    const sanitizedUserNavItems: WHLayoutNavItemDOM[] = sanitizeFeatureConfig(
      layoutLoggedInUserNavItemDOMList,
      environment.devFeatureConfigMap,
    ) as WHLayoutNavItemDOM[];
    layoutConfigService.setLayoutLoggedInUserNavItemDOMList(
      sanitizedUserNavItems,
    );

    // UI ELEMENTS
    sanitizePagesUi(featureConfigMap);
  }

  private initializeEnvironment(environmentModel: IEnvironmentSettings) {
    if (environmentModel.STAGE) {
      environment.production = true;
      environment.stage = environmentModel.STAGE;
    }

    if (environmentModel.API_URL) environment.apiUrl = environmentModel.API_URL;

    if (environmentModel.MGMT_URL)
      environment.mgmtUrl = environmentModel.MGMT_URL;
    if (environmentModel.FLOW_URL)
      environment.flowUrl = environmentModel.FLOW_URL;
    if (environmentModel.AUTH0_DOMAIN && environmentModel.AUTH0_CLIENT_ID) {
      environment.auth0Config.domain = environmentModel.AUTH0_DOMAIN;
      environment.auth0Config.clientId = environmentModel.AUTH0_CLIENT_ID;
    }
    environment.auth0Config.cacheLocation = 'localstorage';
    environment.auth0Config.useRefreshTokens = true;

    if (environmentModel.INSTRUMENTATION_KEY)
      environment.instrumentationKey = environmentModel.INSTRUMENTATION_KEY;
    if (environmentModel.GOOGLE_MAPS_API_KEY)
      environment.googleMapsApiKey = environmentModel.GOOGLE_MAPS_API_KEY;
  }

  checkIfNullValues(environmentModel: IEnvironmentSettings): boolean {
    let isNull = false;
    //Check if any values are null or empty return true if so
    Object.entries(environmentModel).forEach((property: any[]) => {
      if (property[1] === null || property[1] === '') {
        isNull = true;
      }
    });
    return isNull;
  }

  private playSplashScreen(type: string, config?: SplashScreenConfigDOM): void {
    // ADD Splash Screen Element
    this.splashScreenElem = this.document.body.querySelector(
      '#workheld-splash-screen',
    );

    if (!this.splashScreenElem) return;
    this.splashScreenElem.innerHTML = initSplashScreen(type, config);
  }
}
